"use server"

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import PlatformGrid from '../components/grids/PlatformGrid';
import PartnerRequestForm from '../components/forms/PartnerRequestForm';
import Header from '../layouts/Header';

export default function HomePage() {
    const platforms = useMemo(() => [
        {
            title: 'Patient Management',
            desc: 'Seamlessly manage patient intake, scheduling, care teams, and ongoing engagement.',
            extraContent: {
                title: "Patient Management",
                data: [
                    "Patient Records: Search, profiles, charts, and prospect tracking",
                    "Scheduling: Location & provider calendars, staff schedules, appointment history",
                    "Care Team: Account owners, grouped support, coordinated care roles",
                    "Opportunities & Requests: Reorders, patient requests, assignments, care groups"
                ]
            }
        },
        {
            title: 'Electronic Medical Record',
            desc: 'Structured clinical workflows with configurable protocols and real-time clinical insights.',
            extraContent: {
                title: "Electronic Medical Records (EMR)",
                data: [
                    "Comprehensive Patient Profile: Demographics, billing details, service locations, attributes, documents, files, and outside labs",
                    "Clinical Records & Notes: Medical assessments, med lists, notes, problem lists, procedure notes, indicators, and staff/patient-driven activity",
                    "Labs & Diagnostics: Lab orders, results, cases, histories, and integrated order brief history",
                    "Treatment & Medication Management: Treatments, prescriptions (RX), service history, and training records"
                ]
            }
        },
        {
            title: 'Order & Refill Management',
            desc: 'Effortless prescription workflows with end-to-end order tracking and automated refills.',
            extraContent: {
                title: "Order & Refill Management",
                data: [
                    "Order Processing: Worklists, RX/DPR orders, tracking, and invoicing",
                    "Order History: Full visibility into past orders and fulfillment status",
                    "Order Briefs: Product details, pricing, and automated brief generation",
                    "Refills & Reorders: Automated reorders and patient-initiated refill flows"
                ]
            }
        },
        {
            title: 'Product / Inventory Management',
            desc: 'Centralized product, vendor, and inventory oversight to keep operations fully in sync.',
            extraContent: {
                title: "Products & Inventory Management",
                data: [
                    "Product & Vendor Management: Centralized control of products, vendors, and biomarkers",
                    "eCommerce Inventory: Manage products, services, images, and descriptions",
                    "Nutrient Therapy (IV/IMS): IV protocols, inventory tracking, and 503A pharmacy integration",
                    "Quality & Safety: Adverse event tracking and compliance support"
                ]
            }
        },
        {
            title: 'Dashboard / Reporting',
            desc: 'Actionable performance analytics with intelligent recommendations to drive growth.',
            extraContent: {
                title: "Dashboard / Reporting (Condensed)",
                data: [
                    "Financial Reporting: Products, services, locations, and margin performance",
                    "KPI Analytics: Conversion, acquisition, attrition, and activity trends",
                    "Patient Insights: CSAT, lifetime value, and engagement metrics",
                    "eCommerce Reporting: Transaction tracking, integrations, and invoicing visibility"
                ]
            }
        },
        {
            title: 'Payment / Invoice',
            desc: 'Integrated billing with simplified payments, invoicing, and transaction transparency.',
            extraContent: {
                title: "Payment / Invoice",
                data: [
                    "Process Payments: Credit card, POS, and cash",
                    "Manage Promotions: Discounts, subscriptions, and loyalty rewards",
                    "Generate & Track Invoices: Order-based and patient-facing invoices",
                    "Support Integrated Billing: Seamless payment flows across the platform"
                ]
            }
        },
        {
            title: 'Customer Relationship Management',
            desc: 'Targeted communication, segmentation, and retention tools to nurture every patient journey.',
            extraContent: {
                title: "Customer Relationship Management",
                data: [
                    "Multi-Channel Communication: Email, SMS, app push, news posts, and automated messages",
                    "Smart Segmentation: Target by activity, attributes, personas, and location",
                    "Automations & Journeys: Campaigns, complex workflows, opt-out management, and BI-driven triggers",
                    "Loyalty & Engagement: Loyalty programs, points, referrals, and subscription management"
                ]
            }
        },
        {
            title: 'Consents Management',
            desc: 'Create, customize, and manage digital consents across all services and procedures.',
            extraContent: {
                title: "Consent Management",
                data: [
                    "Patient Consents",
                    "Service Consents",
                ]
            }
        },
        {
            title: 'Patient App',
            desc: 'A connected mobile experience for booking, care plans, orders, tracking, and rewards.',
            extraContent: {
                title: "Patient App:",
                data: [
                    "Unified Communication Hub: Push notifications, messaging, news/updates, requests, and automated engagement",
                    "Care & Records Access: Labs, care plans, treatment history, summaries, and referrals",
                    "Commerce & Orders: E-commerce browsing, recommendations, self-checkout, reorders, order tracking, invoices, promos, and rewards",
                    "Appointments & Support: Booking, confirmations, status tracking, interactive chat, BI-driven assistance, and controlled LLM responses",
                ]
            }
        },
    ], []);
    const benefits = useMemo(() => [
        { title: 'New Revenue Channels', desc: 'New income streams through streamlined scheduling, automated order refills, and patient-driven experiences.' },
        { title: 'Clinical Operations', desc: 'Manage every part of your practice with ease bookings, orders, consents, payment history and patient interactions securely and compliantly.' },
        { title: 'Branded Patient Experience', desc: 'Deliver a luxury-grade, fully branded digital interface to your patients.' }
    ], []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-gray-100 text-slate-900">
            {/* NAV */}
            <Header />

            {/* HERO */}
            <section className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center gap-12 py-16 mt-[92px]">
                <div className="w-full md:w-6/12">
                    <motion.h1
                        initial={{ y: 18, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6 }}
                        className="text-4xl md:text-5xl font-extrabold !leading-normal"
                    >
                        Power your practice by streamlining your software
                    </motion.h1>
                    <p className="mt-4 text-lg text-slate-600">Balynced enables practices to offer precision medicine, hormone optimization, and longevity services- effortlessly. Empower your clinicians, elevate patient experience, and expand revenue streams with our end-to-end platform.</p>
                    <div className="mt-8 flex gap-4">
                        <a href="#waitlist" className="px-3 md:px-6 py-3 rounded-lg bg-slate-900 text-white font-medium shadow hover:opacity-95">Request a Demo</a>
                        <a href="#benefits" className="px-3 md:px-6 py-3 rounded-lg border border-slate-300 text-slate-700">Learn More</a>
                    </div>
                </div>

                <div className="w-full md:w-6/12 flex justify-center">
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="w-96 bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-2xl p-6 ring-1 ring-slate-200"
                    >
                        <div className="font-semibold text-lg mb-2">Practice Dashboard</div>
                        <div className="h-2 bg-slate-100 rounded-full mb-3 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-slate-900 to-emerald-500" style={{ width: '68%' }}></div>
                        </div>
                        <div className="grid gap-3 text-xs text-slate-700">
                            <div className="p-3 bg-white rounded-lg shadow-md">Actionable patient data at a glance.</div>
                            <div className="p-3 bg-white rounded-lg shadow-md">Real-time practice performance.</div>
                            <div className="p-3 bg-white rounded-lg shadow-md">Clear visibility into team productivity.</div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* PLATFORM OVERVIEW */}
            <section id="platform" className="scroll-offset max-w-6xl mx-auto py-16">
                <h2 className="px-6 text-2xl font-semibold">A unified platform for modern healthcare practices</h2>
                <p className="px-6 mt-2 text-slate-600 max-w-3xl">Streamline every aspect of concierge medicine — from patient acquisition and telehealth to compounding fulfillment, IV scheduling, and analytics. Balynced Tech Solutions brings operational efficiency and clinical intelligence to your fingertips.</p>
                <PlatformGrid platforms={platforms} />
            </section>

            {/* BENEFITS SECTION */}
            <section id="benefits" className='scroll-offset mx-[20px] xl:mx-0'>
                <div className="mx-auto max-w-6xl px-6 py-16 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl">
                    <h3 className="text-2xl font-semibold text-center">Why practices choose Balynced</h3>
                    <div className="mt-10 grid md:grid-cols-3 gap-6">
                        {benefits.map((b, i) => (
                            <motion.div key={i} className="p-6 bg-white/10 backdrop-blur rounded-2xl" whileHover={{ y: -4 }}>
                                <div className="font-semibold text-lg mb-2">{b.title}</div>
                                <div className="text-sm text-slate-200">{b.desc}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INTEGRATION SECTION */}
            <section id="integration" className="scroll-offset max-w-6xl mx-auto px-6 py-16">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <h3 className="text-2xl font-semibold">Seamless integrations</h3>
                        <p className="mt-3 text-slate-600">Balynced connects effortlessly with existing solutions, labs, and payment systems, ensuring smooth data flow and compliance without disrupting existing workflows.</p>
                        <ul className="mt-6 list-disc pl-5 space-y-2 text-sm text-slate-600">
                            <li>Integrate with major compounding pharmacies and....</li>
                            <li>Secure data syncing and HIPAA compliance</li>
                            <li>White-label options for brand customization</li>
                            <li>Custom Integration available</li>
                        </ul>
                    </div>
                    <div className="rounded-2xl bg-gradient-to-br from-white to-slate-50 shadow-lg p-6">
                        <div className="font-semibold mb-2">Integration Map</div>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="p-3 bg-white rounded-lg transition-all shadow-md hover:shadow-lg">503 A & 503 B Pharmacies</div>
                            <div className="p-3 bg-white rounded-lg transition-all shadow-md hover:shadow-lg">Labs</div>
                            <div className="p-3 bg-white rounded-lg transition-all shadow-md hover:shadow-lg">Telephone Platforms</div>
                            <div className="p-3 bg-white rounded-lg transition-all shadow-md hover:shadow-lg">Payment Gateways</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* LEAD GEN */}
            <section id="waitlist" className="max-w-3xl mx-auto px-6 py-16">
                <div className="rounded-3xl bg-gradient-to-br from-white to-slate-50 p-8 shadow-lg ring-1 ring-slate-200 text-center">
                    <h3 className="text-2xl font-semibold">Join the Provider Network</h3>
                    <p className="mt-2 text-slate-600">Become a Balynced partner and bring concierge longevity care to your patients. Request early access below.</p>

                    <PartnerRequestForm />
                    <div className="mt-6 text-xs text-slate-500">Trusted by progressive practices nationwide • Secure • Scalable • Physician-led</div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-600">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className='flex flex-col gap-2 justify-content-center align-items-center'>
                            <img
                                className="mix-blend-multiply"
                                src={require('../assets/images/Balynced-Transparent.png')}
                                style={{ height: 60, width: "fit-content" }}
                                alt="logo"
                            />
                            <div className="text-xs md:mx-3">Empowering modern medical practices</div>
                        </div>
                    </div>
                    <div className="flex gap-6">
                        <a href="#" className="hover:underline">Privacy</a>
                        <a href="#" className="hover:underline">Terms</a>
                        <a href="#" className="hover:underline">Contact</a>
                    </div>
                </div>
                <div className="mt-6 text-xs text-slate-400">© {new Date().getFullYear()} Balynced Tech. All rights reserved.</div>
            </footer>
        </div>
    );
}
